/**
 * Task Service — offline-first task management.
 *
 * All writes go to IndexedDB first, then enqueue a sync item.
 * All reads come from IndexedDB only (offline-first).
 *
 * @module taskService
 * @see Requirements 2.1, 2.3, 2.8, 2.10, 3.1, 3.3, 3.7, 12.1
 */

import type {
  Task,
  TaskList,
  Tag,
  TaskFilters,
  Priority,
} from '../types';
import { SyncStatus } from '../types/enums';
import { db } from './localDb';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Sort field options for task queries. */
export type TaskSortField = 'dueDate' | 'priority' | 'createdAt' | 'sortOrder' | 'title';

/** Sort direction. */
export type TaskSortDirection = 'asc' | 'desc';

/** Sort configuration for task queries. */
export interface TaskSort {
  field: TaskSortField;
  direction: TaskSortDirection;
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Maximum number of subtasks per task. */
const MAX_SUBTASKS = 20;

/** Maximum number of tags per task. */
const MAX_TAGS = 10;

/** Maximum number of task lists per user. */
const MAX_TASK_LISTS = 50;

/** Maximum title length. */
const MAX_TITLE_LENGTH = 500;

/** Minimum title length. */
const MIN_TITLE_LENGTH = 1;

/** Maximum description length. */
const MAX_DESCRIPTION_LENGTH = 5000;

// ─── Priority ordering (for sort comparisons) ────────────────────────────────

const PRIORITY_ORDER: Record<Priority, number> = {
  high: 0,
  medium: 1,
  low: 2,
  none: 3,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Enqueue a sync item for a task module entity change.
 */
async function enqueueSyncChange(
  entityId: string,
  action: 'create' | 'update' | 'delete',
  payload: Record<string, unknown>
): Promise<void> {
  const size = await db.syncQueue.count();
  if (size >= 1000) {
    throw new Error(
      'Sync queue is full: maximum 1000 queued changes allowed. Connect to sync.'
    );
  }

  await db.syncQueue.add({
    module: 'tasks',
    entityId,
    action,
    timestamp: new Date().toISOString(),
    payload,
    retryCount: 0,
  } as unknown as import('../types').SyncQueueItem);
}

/**
 * Validate a task title.
 */
function validateTitle(title: string): void {
  if (!title || title.length < MIN_TITLE_LENGTH) {
    throw new Error(`Title must be at least ${MIN_TITLE_LENGTH} character(s).`);
  }
  if (title.length > MAX_TITLE_LENGTH) {
    throw new Error(`Title must be at most ${MAX_TITLE_LENGTH} characters.`);
  }
}

/**
 * Validate a task description.
 */
function validateDescription(description: string | undefined): void {
  if (description != null && description.length > MAX_DESCRIPTION_LENGTH) {
    throw new Error(`Description must be at most ${MAX_DESCRIPTION_LENGTH} characters.`);
  }
}

// ─── Task Service ────────────────────────────────────────────────────────────

/**
 * Create a new task. Writes to IndexedDB and enqueues sync.
 */
export async function createTask(
  task: Omit<Task, 'taskId' | 'createdAt' | 'updatedAt' | 'syncStatus'>
): Promise<Task> {
  validateTitle(task.title);
  validateDescription(task.description);

  if (task.tags && task.tags.length > MAX_TAGS) {
    throw new Error(`A task can have at most ${MAX_TAGS} tags.`);
  }

  if (task.subtasks && task.subtasks.length > MAX_SUBTASKS) {
    throw new Error(`A task can have at most ${MAX_SUBTASKS} subtasks.`);
  }

  const now = new Date().toISOString();
  const newTask: Task = {
    taskId: crypto.randomUUID(),
    ...task,
    description: task.description ?? '',
    priority: task.priority ?? 'none',
    dueDate: task.dueDate ?? null,
    dueTime: task.dueTime ?? null,
    listId: task.listId ?? null,
    tags: task.tags ?? [],
    subtasks: task.subtasks ?? [],
    isCompleted: task.isCompleted ?? false,
    completedAt: task.completedAt ?? null,
    isUrgent: task.isUrgent ?? false,
    isImportant: task.isImportant ?? false,
    sortOrder: task.sortOrder ?? 0,
    linkedJournalDate: task.linkedJournalDate ?? null,
    focusMinutes: task.focusMinutes ?? 0,
    deleted: false,
    createdAt: now,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  await db.tasks.put(newTask);

  await enqueueSyncChange(newTask.taskId, 'create', newTask as unknown as Record<string, unknown>);

  return newTask;
}

/**
 * Get a single task by ID.
 */
export async function getTask(taskId: string): Promise<Task | undefined> {
  const task = await db.tasks.get(taskId);
  if (task && task.deleted) {
    return undefined;
  }
  return task;
}

/**
 * Update an existing task. Writes to IndexedDB and enqueues sync.
 */
export async function updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
  const existing = await db.tasks.get(taskId);
  if (!existing || existing.deleted) {
    throw new Error(`Task not found: ${taskId}`);
  }

  if (updates.title !== undefined) {
    validateTitle(updates.title);
  }

  if (updates.description !== undefined) {
    validateDescription(updates.description);
  }

  if (updates.tags !== undefined && updates.tags.length > MAX_TAGS) {
    throw new Error(`A task can have at most ${MAX_TAGS} tags.`);
  }

  if (updates.subtasks !== undefined && updates.subtasks.length > MAX_SUBTASKS) {
    throw new Error(`A task can have at most ${MAX_SUBTASKS} subtasks.`);
  }

  const now = new Date().toISOString();
  const updatedTask: Task = {
    ...existing,
    ...updates,
    taskId: existing.taskId, // prevent overwriting ID
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  await db.tasks.put(updatedTask);

  await enqueueSyncChange(taskId, 'update', updatedTask as unknown as Record<string, unknown>);

  return updatedTask;
}

/**
 * Soft-delete a task. Sets deleted=true so it syncs as a deletion.
 */
export async function deleteTask(taskId: string): Promise<void> {
  const existing = await db.tasks.get(taskId);
  if (!existing) {
    return;
  }

  const now = new Date().toISOString();
  const deletedTask: Task = {
    ...existing,
    deleted: true,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  await db.tasks.put(deletedTask);

  await enqueueSyncChange(taskId, 'delete', { taskId });
}

/**
 * List tasks with optional filtering and sorting.
 * Filter logic: all fields combine with AND.
 * Only returns non-deleted tasks.
 */
export async function listTasks(filters?: TaskFilters, sort?: TaskSort): Promise<Task[]> {
  let tasks = await db.tasks.where('deleted').equals(0).toArray();

  // Dexie stores boolean as 0/1, so also catch false
  // Fallback: get all and filter out deleted
  if (tasks.length === 0) {
    const allTasks = await db.tasks.toArray();
    tasks = allTasks.filter((t) => !t.deleted);
  } else {
    tasks = tasks.filter((t) => !t.deleted);
  }

  // Apply filters (AND combination)
  if (filters) {
    tasks = applyFilters(tasks, filters);
  }

  // Apply sort
  if (sort) {
    tasks = applySortToTasks(tasks, sort);
  }

  return tasks;
}

/**
 * Search tasks by text. Case-insensitive search in title and description.
 * Only returns non-deleted tasks.
 */
export async function searchTasks(query: string): Promise<Task[]> {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const allTasks = await db.tasks.toArray();
  const lowerQuery = query.toLowerCase();

  return allTasks.filter(
    (t) =>
      !t.deleted &&
      (t.title.toLowerCase().includes(lowerQuery) ||
        t.description.toLowerCase().includes(lowerQuery))
  );
}

// ─── Task List Service ───────────────────────────────────────────────────────

/**
 * Create a new task list. Enforces max 50 lists per user.
 */
export async function createTaskList(
  list: Omit<TaskList, 'listId' | 'createdAt' | 'updatedAt'>
): Promise<TaskList> {
  if (!list.name || list.name.length < 1 || list.name.length > 100) {
    throw new Error('Task list name must be between 1 and 100 characters.');
  }

  // Enforce capacity
  const existingLists = await db.taskLists
    .where('userId')
    .equals(list.userId)
    .toArray();

  if (existingLists.length >= MAX_TASK_LISTS) {
    throw new Error(`Maximum of ${MAX_TASK_LISTS} task lists allowed.`);
  }

  const now = new Date().toISOString();
  const newList: TaskList = {
    listId: crypto.randomUUID(),
    ...list,
    createdAt: now,
    updatedAt: now,
  };

  await db.taskLists.put(newList);

  await enqueueSyncChange(newList.listId, 'create', newList as unknown as Record<string, unknown>);

  return newList;
}

/**
 * Get all task lists for the current user.
 */
export async function getTaskLists(): Promise<TaskList[]> {
  return db.taskLists.toArray();
}

/**
 * Update an existing task list.
 */
export async function updateTaskList(
  listId: string,
  updates: Partial<TaskList>
): Promise<TaskList> {
  const existing = await db.taskLists.get(listId);
  if (!existing) {
    throw new Error(`Task list not found: ${listId}`);
  }

  if (updates.name !== undefined) {
    if (!updates.name || updates.name.length < 1 || updates.name.length > 100) {
      throw new Error('Task list name must be between 1 and 100 characters.');
    }
  }

  const now = new Date().toISOString();
  const updatedList: TaskList = {
    ...existing,
    ...updates,
    listId: existing.listId,
    updatedAt: now,
  };

  await db.taskLists.put(updatedList);

  await enqueueSyncChange(listId, 'update', updatedList as unknown as Record<string, unknown>);

  return updatedList;
}

/**
 * Delete a task list.
 */
export async function deleteTaskList(listId: string): Promise<void> {
  const existing = await db.taskLists.get(listId);
  if (!existing) {
    return;
  }

  await db.taskLists.delete(listId);

  await enqueueSyncChange(listId, 'delete', { listId });
}

// ─── Tag Service ─────────────────────────────────────────────────────────────

/**
 * Create a new tag.
 */
export async function createTag(tag: Omit<Tag, 'tagId'>): Promise<Tag> {
  if (!tag.name || tag.name.length < 1 || tag.name.length > 50) {
    throw new Error('Tag name must be between 1 and 50 characters.');
  }

  const newTag: Tag = {
    tagId: crypto.randomUUID(),
    ...tag,
  };

  await db.tags.put(newTag);

  await enqueueSyncChange(newTag.tagId, 'create', newTag as unknown as Record<string, unknown>);

  return newTag;
}

/**
 * Get all tags.
 */
export async function getTags(): Promise<Tag[]> {
  return db.tags.toArray();
}

/**
 * Delete a tag.
 */
export async function deleteTag(tagId: string): Promise<void> {
  await db.tags.delete(tagId);

  await enqueueSyncChange(tagId, 'delete', { tagId });
}

// ─── Filter Logic ────────────────────────────────────────────────────────────

/**
 * Apply TaskFilters using AND logic. All specified filter fields must match.
 */
export function applyFilters(tasks: Task[], filters: TaskFilters): Task[] {
  return tasks.filter((task) => {
    // List filter
    if (filters.listId !== undefined && task.listId !== filters.listId) {
      return false;
    }

    // Priority filter
    if (filters.priority !== undefined && task.priority !== filters.priority) {
      return false;
    }

    // Due date range filter
    if (filters.dueDateStart !== undefined) {
      if (task.dueDate === null || task.dueDate < filters.dueDateStart) {
        return false;
      }
    }

    if (filters.dueDateEnd !== undefined) {
      if (task.dueDate === null || task.dueDate > filters.dueDateEnd) {
        return false;
      }
    }

    // Tag filter: task must have at least one of the specified tag IDs
    if (filters.tagIds !== undefined && filters.tagIds.length > 0) {
      const hasMatchingTag = filters.tagIds.some((tagId) => task.tags.includes(tagId));
      if (!hasMatchingTag) {
        return false;
      }
    }

    // Completion status filter
    if (filters.isCompleted !== undefined && task.isCompleted !== filters.isCompleted) {
      return false;
    }

    // Text search: case-insensitive in title and description
    if (filters.searchText !== undefined && filters.searchText.trim().length > 0) {
      const lowerSearch = filters.searchText.toLowerCase();
      const matchesTitle = task.title.toLowerCase().includes(lowerSearch);
      const matchesDescription = task.description.toLowerCase().includes(lowerSearch);
      if (!matchesTitle && !matchesDescription) {
        return false;
      }
    }

    return true;
  });
}

// ─── Sort Logic ──────────────────────────────────────────────────────────────

/**
 * Apply sort to a list of tasks. Uses stable sort.
 */
export function applySortToTasks(tasks: Task[], sort: TaskSort): Task[] {
  const sorted = [...tasks];

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (sort.field) {
      case 'dueDate': {
        // Null dates always go to the end regardless of direction
        if (a.dueDate === null && b.dueDate === null) return 0;
        if (a.dueDate === null) return 1;
        if (b.dueDate === null) return -1;
        comparison = a.dueDate.localeCompare(b.dueDate);
        break;
      }
      case 'priority': {
        comparison = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
        break;
      }
      case 'createdAt': {
        comparison = a.createdAt.localeCompare(b.createdAt);
        break;
      }
      case 'sortOrder': {
        comparison = a.sortOrder - b.sortOrder;
        break;
      }
      case 'title': {
        comparison = a.title.localeCompare(b.title);
        break;
      }
    }

    return sort.direction === 'desc' ? -comparison : comparison;
  });

  return sorted;
}
